import { EncircledText, SearchBar } from '@amelio/ui-library';
import { Trans } from 'react-i18next';

import type { ProfileFor } from './ProfileSelector';
import type { ReactNode } from 'react';

import { BackLink } from '@/components/BackLink';
import { useAmelioTranslations } from '@/hooks';

/** The subtitles that circle the word saying whose profile is edited. */
type EncircledSubtitleKey =
    | 'competencies.competencyProfilesAdmin.selector.currentSubtitle'
    | 'competencies.competencyProfilesAdmin.selector.targetSubtitle';

type ProfileSelectorHeaderProps = {
    employeeName: string;
    profileFor: ProfileFor;
    isEditing: boolean;
    isSubmitting: boolean;
    showSearch: boolean;
    search: string;
    onSearchChange: (value: string) => void;
    onCancel: () => void;
    /** The caller's own fields, between the subtitle and the search that filters the profiles. */
    children?: ReactNode;
};

export const ProfileSelectorHeader = ({
    employeeName,
    profileFor,
    isEditing,
    isSubmitting,
    showSearch,
    search,
    onSearchChange,
    onCancel,
    children,
}: ProfileSelectorHeaderProps) => {
    const { t } = useAmelioTranslations();

    // Whose profile the screen edits, the word that says it circled.
    const encircledSubtitle = (i18nKey: EncircledSubtitleKey) => (
        <Trans
            i18nKey={i18nKey}
            values={{ name: employeeName }}
            components={{
                // `Trans` fills each tag with the words the translation wraps in it.
                encircled: (
                    <EncircledText dataTestId={`roster-selector-${profileFor}-role`}>
                        {null}
                    </EncircledText>
                ),
                employee: <span className={'text-navy font-medium'} />,
            }}
        />
    );

    const readSubtitle = () => {
        if (profileFor === 'target') {
            return encircledSubtitle(
                'competencies.competencyProfilesAdmin.selector.targetSubtitle',
            );
        }
        if (isEditing) {
            return encircledSubtitle(
                'competencies.competencyProfilesAdmin.selector.currentSubtitle',
            );
        }
        return t('competencies.competencyProfilesAdmin.selector.assignSubtitle', {
            name: employeeName,
        });
    };
    const subtitle = readSubtitle();

    return (
        <>
            <BackLink
                label={t('competencies.competencyProfilesAdmin.back')}
                onClick={onCancel}
                disabled={isSubmitting}
                className={'self-start'}
                dataTestId={'roster-selector-cancel'}
            />

            <div>
                <p className={'text-navy text-2xl leading-tight font-medium sm:!text-[30px]'}>
                    {t(
                        isEditing || profileFor === 'target'
                            ? 'competencies.competencyProfilesAdmin.selector.title'
                            : 'competencies.competencyProfilesAdmin.selector.assignTitle',
                        { name: employeeName },
                    )}
                </p>

                {subtitle && <p className={'mt-2 text-[16px] text-gray-600'}>{subtitle}</p>}

                {children && <div className={'mt-6'}>{children}</div>}

                {showSearch && (
                    <div className={'mt-4 max-w-md'}>
                        <SearchBar
                            size={'sm'}
                            value={search}
                            onChange={onSearchChange}
                            placeholder={t(
                                'competencies.competencyProfilesAdmin.selector.searchPlaceholder',
                            )}
                            className={'!h-[45px] !border-gray-200'}
                            dataTestId={'roster-selector-search'}
                        />
                    </div>
                )}
            </div>
        </>
    );
};
